import { useCallback, useMemo, useState } from 'react'
import {
  Box, Card, Typography, Checkbox, TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Chip, Tooltip,
  FormControl, Select, MenuItem, Button, Snackbar, TableSortLabel, CircularProgress,
} from '@mui/material'
import { Search, WarningAmber, Replay, KeyboardArrowDown, LockOutlined } from '@mui/icons-material'
import { footerTotals, dosStatus, hasRunRate } from './fourToTwelve'
import {
  visibleRows, sortRows, bulkTargets, ACTIONS, scopeMonths, keptEditCount, bulkRule, appliedMessage,
  reviewTooltip, monthHeading, demandChange, abbreviate, selectAllState,
  SUB_COLS, subSortKey, DEFAULT_SORT, dosCellStyle, deltaColor, parsePercent, PCT_LIMITS, isSelectable, rowTags,
} from './nextThreeMonths'
import { BASES } from './fourToTwelve'
import UnitsCell from './UnitsCell'
import { frozenWidths, tableWidth, COL_W, MONTH_TINT, TINT_ON, MONTH_DIVIDER, HAIRLINE, SKU_CARD_SX } from './gridLayout'

const GREEN = '#15803D'
// FAR-88 S16: the app's tokens where they exist (theme primary, not the
// mockup's #4B9C3D); the mockup's values only where the theme has none.
const PRIMARY = 'primary.main'
const PRIMARY_DARK = 'primary.dark'
// FAR-88 S7: the mockup's month tint, solid where it sits on sticky rows.
const TINT = MONTH_TINT // over white body cells
const BAND = '#EEF6EE' // FAR-83 §4.1 green header band
const BAND_TINT = TINT_ON.green
const STRIP = '#F6F8F5' // FAR-88 S12 column-header strip
const STRIP_TINT = TINT_ON.strip
const FOOT = '#F8FAFC' // footer rows (grey.50)
const FOOT_TINT = TINT_ON.foot
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString())
const LINE_BORDER = MONTH_DIVIDER
// FAR-88 S13: a checked row is pale green across the whole row, over the tint.
const CHECKED = 'primary.light'
// FAR-88 U2: amber warning tags (S16; the theme has no amber tint).
const AMBER = { bgcolor: '#FCF4CC', color: '#6B5410', border: '1px solid #E5CE74' }

// FAR-88 U2 / S14: the DOS column. Min, Target and Max separated by spaces,
// the Target in a pale-green capsule. A made-to-order band (0/0/0) reads
// "0 0 0"; a SKU with no usable band reads "—".
function DosBand({ row }) {
  if (row.isDemandOnly) return '—'
  const min = row.dosMin || 0
  const tgt = row.dosTarget || 0
  const max = row.dosMax || 0
  if (!row.isMadeToOrder && !min && !max) return '—'
  // A banded SKU whose target didn't arrive shows "–", never a green 0
  // (0 is only real on a made-to-order 0/0/0 band).
  const target = tgt > 0 || row.isMadeToOrder ? tgt : '–'
  return (
    <span>
      {min}{' '}
      <Box component="span" sx={{ bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 700, borderRadius: 4, px: 0.75, py: '1px' }}>
        {target}
      </Box>{' '}
      {max}
    </span>
  )
}

// FAR-77: the Next 3 Months tab's SKU Plan. As FAR-76's 4–18 card, plus search,
// Brite / Line / Rate/hr, four values a month (DEMAND, UNITS, INVENTORY, DOS),
// the always-visible bulk bar, and the §5.3 review indicator (an icon, not a
// banner). Line and Rate/hr are the primary line's, read-only: moving a SKU to
// another line waits for the other lines' run rates (Mark, 2026-10-20).
//
// Layout (FAR-83 §2/§4.1, FAR-77 §5.1–5.2, and the mockup), all inside one
// scroll box so the months line up and scroll together:
// - header (sticky top): the green header band (title, review indicator, the
//   month names), the toolbar (search, filters, bulk bar), the column headers;
// - the rows;
// - footer totals (sticky bottom).
// Every cell sets its own background: a row-level `& td` rule would be more
// specific than a cell's own sx and override the month tint.
export default function NextThreeMonthsCard({
  rows, months, basePlan, line, onLineChange, lines, baseRowsByItem, modifications,
  selected, onSelect, editCounts, onEditUnits, onRevertSku, onApplyRule, review, onBackToBasePlan, readOnly,
  withLines = true, testId = 'next3', forecastChanges, modificationName, scrollRef,
  lockMessage = '', openEditCounts, revertTooltip, viewOnly = false,
}) {
  // FAR-88: lockMessage is set while this tab is approved -- the grid is then
  // read-only and says why. openEditCounts: per SKU, the typed months no
  // approved tab locks (the "n edits" pill reverts only those).
  // FAR-89 #1: viewOnly is the Base Plan tab -- the Base Plan as generated,
  // with nothing to edit: no checkbox column, no bulk bar, units as plain
  // numbers, no Back to Base Plan and no Δ vs Base Plan row. Search, the
  // filter pills and sorting stay.
  // FAR-83: the Months 4–18 tab uses this card without its line features (no
  // Line or Rate/hr column, no Line filter). Planning there is by SKU.
  // FAR-83 #13: fixed widths shared with Capacity and Risk (gridLayout.js), so
  // the months line up. The <colgroup> sets them; cells can't grow.
  const W = frozenWidths(withLines, !viewOnly)
  const leftOf = (i) => W.slice(0, i).reduce((a, b) => a + b, 0)
  // Frozen cells: sticky left, above the month cells that scroll under them.
  const stick = (i, bg = 'background.paper') => ({ position: 'sticky', left: leftOf(i), zIndex: 2, bgcolor: bg })
  // Frozen column i as numbered with the checkbox column (0 = checkbox), so
  // the same indexes work when viewOnly drops it.
  const col = (i, bg) => stick(i - (viewOnly ? 1 : 0), bg)
  const frozen = W.length
  const [status, setStatus] = useState('all')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [action, setAction] = useState('increase')
  const [scope, setScope] = useState('all')
  // FAR-88 T3: the percent is typed; pct is null while the text isn't valid.
  const [pctText, setPctText] = useState('+10')
  const pct = parsePercent(pctText, action)
  const [basis, setBasis] = useState('Min')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')
  // The scroll box's visible width: the toolbar is pinned to it, so the bulk
  // bar's right edge lines up with the grid's (FAR-83 §4.1).
  const [viewW, setViewW] = useState(0)
  const boxRef = useCallback((node) => {
    const cleanup = scrollRef?.(node)
    if (!node) return cleanup
    const measure = () => setViewW(node.clientWidth)
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    measure()
    return () => {
      ro.disconnect()
      if (typeof cleanup === 'function') cleanup()
    }
  }, [scrollRef])

  const shown = useMemo(
    () => sortRows(visibleRows(rows, months, { line: withLines ? line : '', status, query }), sort.key, sort.dir),
    [rows, months, line, status, query, sort, withLines],
  )
  const totals = useMemo(() => footerTotals(rows, months, baseRowsByItem), [rows, months, baseRowsByItem])
  const targets = useMemo(() => bulkTargets(shown, selected), [shown, selected])
  const selectable = shown.filter(isSelectable) // FAR-88 U1/U2
  const checkState = selectAllState(selectable, selected) // FAR-83 §4.2: none / some / all

  const setActionKeepingPct = (a) => {
    setAction(a)
    setPctText(a === 'increase' ? '+10' : '100')
  }

  const apply = async () => {
    if (!targets.length || busy || pct == null) return
    const kept = keptEditCount(modifications, targets, scopeMonths(months, scope))
    setBusy(true)
    try {
      const ok = await onApplyRule(bulkRule({ action, pct, basis, items: targets, months, scope }))
      if (ok !== false) {
        setToast(appliedMessage(targets.length, kept, modificationName))
        onSelect?.(targets, false)
      }
    } finally {
      setBusy(false)
    }
  }

  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))
  const head = (key, text, align = 'center') => (
    <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => sortBy(key)}
      sx={{ justifyContent: align === 'left' ? 'flex-start' : 'center', '& .MuiTableSortLabel-icon': { fontSize: 14, mx: 0.25 } }}>
      {text}
    </TableSortLabel>
  )
  const cellSx = { textAlign: 'center', px: 0.5, fontSize: '0.8rem' }
  // A body cell's background: the checked-row green wins over the month tint.
  const tint = (i, checked = false) => ({ bgcolor: checked ? CHECKED : i % 2 === 1 ? TINT : 'background.paper' })
  // FAR-88 G2 / S12: every column label in one style -- 11px uppercase,
  // letter-spaced, gray, semibold -- on a pale strip with a hairline below.
  const colHead = {
    fontSize: '11px', fontWeight: 600, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase',
    borderBottom: `1px solid ${HAIRLINE} !important`,
    '& .MuiTableSortLabel-icon': { color: '#B8BFB6 !important' },
  }
  // FAR-88 S13: the theme's green checkbox, 18px.
  const checkSx = { p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 } }

  const searchBox = (
    <TextField size="small" placeholder="Search description, item # or Brite" value={query} onChange={(e) => setQuery(e.target.value)}
      inputProps={{ 'aria-label': 'Search SKUs' }}
      // FAR-88 T1 / S10: wide enough for the whole placeholder; a rounded pill.
      sx={{ width: 340, '& .MuiOutlinedInput-root': { borderRadius: 4, height: 32, fontSize: '0.8rem' } }}
      InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
  )
  // FAR-88 S10: a rounded capsule on a pale background; the active pill is
  // solid green with white bold text, the others plain dark text.
  const filters = (
    <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)} aria-label="DOS status"
      sx={{
        bgcolor: '#F6F8F5', borderRadius: 4, p: '2px', gap: '2px', height: 32,
        '& .MuiToggleButton-root': {
          border: 0, borderRadius: '16px !important', px: 1.25, py: 0, textTransform: 'none', fontSize: '0.8rem', color: 'text.primary',
          '&.Mui-selected, &.Mui-selected:hover': { bgcolor: PRIMARY, color: '#fff', fontWeight: 700 },
        },
      }}>
      <ToggleButton value="all">All SKUs</ToggleButton>
      <ToggleButton value="below">Below Min DOS</ToggleButton>
      <ToggleButton value="above">Above Max DOS</ToggleButton>
      {/* FAR-88 U1 / Q4: isolate SKUs that can't be scheduled. */}
      <ToggleButton value="norate">No Run Rate</ToggleButton>
    </ToggleButtonGroup>
  )
  // FAR-88 S10: toolbar controls about 32px high with 13px text, a 6px radius
  // and thin chevrons (not filled triangles).
  const compact = { height: 32, fontSize: '0.8125rem', borderRadius: '6px' }
  const selectProps = { size: 'small', IconComponent: KeyboardArrowDown, sx: compact }
  // FAR-88 T6 / S10: an outlined pill that shows its selection, "Line: All
  // Lines" / "Line: Line 2" ("Line:" muted, the value bold).
  const lineName = (l) => (l ? l.replace('LINE', 'Line') : 'All Lines')
  const lineFilter = withLines && (
    <Select size="small" IconComponent={KeyboardArrowDown} value={line} onChange={(e) => onLineChange(e.target.value)} displayEmpty
      inputProps={{ 'aria-label': 'Line' }} data-testid={`${testId}-line-filter`}
      renderValue={(v) => (
        <span><Box component="span" sx={{ color: 'text.secondary' }}>Line: </Box><Box component="span" sx={{ fontWeight: 700 }}>{lineName(v)}</Box></span>
      )}
      sx={{ borderRadius: 4, fontSize: '0.8125rem', height: 32, minWidth: 150 }}>
      <MenuItem value="">All Lines</MenuItem>
      {lines.map((l) => <MenuItem key={l} value={l}>{lineName(l)}</MenuItem>)}
    </Select>
  )
  // FAR-83 §4.5 / FAR-77 §5.2: "Modify Selected [action] in [months] by [%]
  // Apply". No selected-count and no helper text.
  const bulkBar = (
    // ml auto: right-aligned, also when a narrow window wraps it to a second line.
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 'auto' }} data-testid={`${testId}-bulk-bar`}>
      {readOnly && lockMessage && (
        // FAR-88: an approved tab is read-only; say why next to the controls it disables.
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mr: 1 }} data-testid={`${testId}-locked`}>
          <LockOutlined sx={{ fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{lockMessage}</Typography>
        </Box>
      )}
      <Typography variant="body2" sx={{ fontWeight: 700 }}>Modify Selected</Typography>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select {...selectProps} value={action} onChange={(e) => setActionKeepingPct(e.target.value)} inputProps={{ 'aria-label': 'Action' }}>
          {ACTIONS.map((a) => <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>)}
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary">in</Typography>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select {...selectProps} value={scope} onChange={(e) => setScope(e.target.value)} inputProps={{ 'aria-label': 'Months' }}>
          <MenuItem value="all">All months</MenuItem>
          {months.map((m) => <MenuItem key={m.iso} value={m.iso}>{m.label}</MenuItem>)}
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary">by</Typography>
      {/* FAR-88 T3 / S10: a small white box, "+10 %". */}
      <TextField size="small" value={pctText} onChange={(e) => setPctText(e.target.value)}
        error={pct == null} data-testid={`${testId}-percent`}
        inputProps={{ 'aria-label': 'Percent', inputMode: 'decimal', style: { textAlign: 'right' } }}
        helperText={pct == null ? `${PCT_LIMITS[action][0]} to ${PCT_LIMITS[action][1]}` : undefined}
        FormHelperTextProps={{ sx: { position: 'absolute', top: '100%', m: 0, whiteSpace: 'nowrap' } }}
        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        sx={{ width: 84, position: 'relative', '& .MuiOutlinedInput-root': compact }} />
      {action === 'dos' && (
        <>
          <Typography variant="body2">of</Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select {...selectProps} value={basis} onChange={(e) => setBasis(e.target.value)} inputProps={{ 'aria-label': 'DOS basis' }}>
              {BASES.map((b) => <MenuItem key={b} value={b}>{b} DOS</MenuItem>)}
            </Select>
          </FormControl>
        </>
      )}
      <Tooltip title={readOnly ? lockMessage : ''}>
        <span>
          <Button size="small" variant="contained" disabled={readOnly || targets.length === 0 || busy || pct == null} onClick={apply}
            data-testid={`${testId}-apply`} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
            // FAR-88 S11: a solid green pill; gray only while disabled.
            sx={{ bgcolor: PRIMARY, borderRadius: 4, px: 2, '&:hover': { bgcolor: PRIMARY_DARK } }}>Apply</Button>
        </span>
      </Tooltip>
    </Box>
  )

  return (
    <Card sx={SKU_CARD_SX} data-testid={`${testId}-card`}>
      {/* Scrolls sideways with Capacity and Risk (useSyncedScrollX). */}
      <Box ref={boxRef} sx={{ overflow: 'auto', maxHeight: '70vh', scrollbarGutter: 'stable' }}>
        <Box component="table" sx={{
          borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: tableWidth(withLines, months.length, !viewOnly),
          '& td, & th': { borderBottom: '1px solid', borderBottomColor: HAIRLINE, py: 0.5, overflow: 'hidden' },
        }}>
          <colgroup>
            {W.map((w, i) => <col key={`f${i}`} style={{ width: w }} />)}
            {months.map((m) => SUB_COLS.map((s) => <col key={`${m.key}${s.field}`} style={{ width: COL_W }} />))}
          </colgroup>
          {/* The whole header stays in view while rows scroll (§2). */}
          <Box component="thead" sx={{ position: 'sticky', top: 0, zIndex: 3 }}>
            {/* §4.1 header band: title and review indicator, then the months. */}
            <Box component="tr" data-testid={`${testId}-band`}>
              <Box component="th" colSpan={frozen} sx={{ ...stick(0, BAND), textAlign: 'left', px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: 0.5 }}
                    data-testid={`${testId}-count`}>
                    SKU Plan · {shown.length} of {rows.length}
                  </Typography>
                  {!viewOnly && review?.length > 0 && (
                    <>
                      <Tooltip title={reviewTooltip(review.length)}>
                        <WarningAmber sx={{ color: '#D97706' }} data-testid={`${testId}-review-icon`} aria-label={reviewTooltip(review.length)} />
                      </Tooltip>
                      {!readOnly && (
                        <Button size="small" variant="contained" onClick={onBackToBasePlan} data-testid={`${testId}-back-to-base`}
                          sx={{ bgcolor: GREEN, borderRadius: 4, '&:hover': { bgcolor: '#166534' } }}>Back to Base Plan</Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
              {months.map((m, i) => (
                <Box component="th" key={m.key} colSpan={SUB_COLS.length} data-month-head={m.key}
                  sx={{ ...LINE_BORDER, fontSize: '0.8rem', bgcolor: i % 2 === 1 ? BAND_TINT : BAND }}>
                  {monthHeading(m, i, basePlan)}
                </Box>
              ))}
            </Box>
            {/* §4.1 toolbar. Pinned to the visible width, so it doesn't scroll
                sideways and the bulk bar ends at the grid's right edge. */}
            <Box component="tr">
              <Box component="td" colSpan={frozen + months.length * SUB_COLS.length}
                sx={{ p: 0, bgcolor: 'background.paper', overflow: 'visible !important' }}>
                <Box sx={{
                  position: 'sticky', left: 0, width: viewW || '100%', boxSizing: 'border-box',
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, flexWrap: 'wrap',
                }} data-testid={`${testId}-toolbar`}>
                  {withLines ? (
                    // FAR-77 §5.2: search above the filter row.
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {searchBox}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>{filters}{lineFilter}</Box>
                    </Box>
                  ) : (
                    // FAR-83 §4.1: one line.
                    <>{searchBox}{filters}</>
                  )}
                  {!viewOnly && bulkBar}
                </Box>
              </Box>
            </Box>
            {/* Column headers, each sortable (§4.2). */}
            <Box component="tr">
              {!viewOnly && <Box component="th" sx={{ ...stick(0, STRIP), ...colHead, px: 0 }}>
                <Checkbox size="small" sx={checkSx} checked={checkState === 'all'} indeterminate={checkState === 'some'} disabled={readOnly}
                  inputProps={{ 'aria-label': 'Select all shown' }}
                  // some -> clears, like none -> all: a click on a part-checked box empties it.
                  onChange={() => onSelect?.(selectable.map((r) => r.itemNumber), checkState === 'none')} />
              </Box>}
              <Box component="th" sx={{ ...col(1, STRIP), ...colHead, textAlign: 'left', px: 1 }}>{head('description', 'Item', 'left')}</Box>
              <Box component="th" sx={{ ...col(2, STRIP), ...colHead }}>{head('briteCode', 'Brite')}</Box>
              {withLines && <Box component="th" sx={{ ...col(3, STRIP), ...colHead }}>{head('line', 'Line')}</Box>}
              <Box component="th" sx={{ ...col(withLines ? 4 : 3, STRIP), ...colHead }}>{head('dosMin', 'DOS')}</Box>
              {withLines && <Box component="th" sx={{ ...col(5, STRIP), ...colHead }}>{head('runRateCasesPerHour', 'Rate/hr')}</Box>}
              {months.map((m, i) => SUB_COLS.map((s, j) => (
                <Box component="th" key={`${m.key}${s.field}`}
                  sx={{ ...cellSx, ...colHead, px: 0.25, ...(j === 0 ? LINE_BORDER : {}), bgcolor: i % 2 === 1 ? STRIP_TINT : STRIP }}>
                  {head(subSortKey(m, s), s.label)}
                </Box>
              )))}
            </Box>
          </Box>
          <tbody>
            {shown.map((r) => {
              const checked = !!selected?.has(r.itemNumber)
              const bg = checked ? CHECKED : 'background.paper'
              const noRate = !hasRunRate(r)
              return (
              <Box component="tr" key={r.itemNumber} data-testid={`${testId}-row-${r.itemNumber}`} data-checked={checked || undefined}>
                {!viewOnly && <Box component="td" sx={{ ...stick(0, bg), px: 0 }}>
                  {/* FAR-88 U1/U2: only standard SKUs take bulk changes. */}
                  <Checkbox size="small" sx={checkSx} checked={checked} disabled={readOnly || !isSelectable(r)}
                    inputProps={{ 'aria-label': `Select ${r.itemNumber}` }}
                    onChange={(e) => onSelect?.([r.itemNumber], e.target.checked)} />
                </Box>}
                <Box component="td" sx={{ ...col(1, bg), textAlign: 'left', px: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>{r.description}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="primary">{r.itemNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">· {r.baseUom || 'CS'}</Typography>
                    {/* FAR-88 U2: gray "Made to order" (a status); amber "No DOS band" / "No run rate" (warnings). */}
                    {rowTags(r).map((t) => {
                      const chip = (
                        <Chip key={t.key} size="small" label={t.label} data-tag={t.key}
                          sx={{ height: 16, fontSize: '0.6rem', ...(t.tone === 'amber' ? AMBER : {}) }} />
                      )
                      return t.tooltip ? <Tooltip key={t.key} title={t.tooltip}>{chip}</Tooltip> : chip
                    })}
                    {editCounts?.[r.itemNumber] > 0 && (() => {
                      // §4.4: the "n edits" pill (refresh icon); clicking it reverts the SKU
                      // -- only its months no approved tab locks (FAR-88).
                      const inert = readOnly || (openEditCounts && !openEditCounts[r.itemNumber])
                      const tip = readOnly ? lockMessage : (revertTooltip?.(r.itemNumber) || 'Revert this SKU to the Base Plan')
                      return (
                      <Tooltip title={tip}>
                        <Chip size="small" variant="outlined" icon={<Replay />} data-testid={`${testId}-edits-${r.itemNumber}`}
                          data-inert={inert ? 'true' : undefined}
                          label={`${editCounts[r.itemNumber]} edit${editCounts[r.itemNumber] === 1 ? '' : 's'}`}
                          onClick={inert ? undefined : () => onRevertSku?.(r.itemNumber)}
                          sx={{ height: 16, fontSize: '0.6rem', color: GREEN, borderColor: GREEN, '& .MuiChip-icon': { fontSize: 11, color: GREEN, ml: 0.5 } }} />
                      </Tooltip>
                      )
                    })()}
                  </Box>
                </Box>
                <Box component="td" sx={{ ...col(2, bg), textAlign: 'center', fontSize: '0.75rem' }}>{r.briteCode || '—'}</Box>
                {withLines && (
                  <Box component="td" sx={{ ...col(3, bg), textAlign: 'center' }}>
                    {/* FAR-88 U1: no run rate, no line. */}
                    {noRate || !r.line ? '—' : (
                      <Tooltip title="Primary line. Moving to another line comes with its run rates.">
                        <Chip size="small" label={r.line.replace('LINE ', 'L')} sx={{ height: 18, fontSize: '0.7rem' }} />
                      </Tooltip>
                    )}
                  </Box>
                )}
                <Box component="td" sx={{ ...col(withLines ? 4 : 3, bg), textAlign: 'center', fontSize: '0.75rem' }} data-dos-band>
                  <DosBand row={r} />
                </Box>
                {withLines && <Box component="td" sx={{ ...col(5, bg), textAlign: 'center', fontSize: '0.75rem' }}>{noRate ? '—' : num(r.runRateCasesPerHour)}</Box>}
                {months.map((m, i) => {
                  const dos = r[`${m.key}_dos`]
                  const st = dosStatus(r, dos, m.key)
                  const t = tint(i, checked)
                  return [
                    (() => {
                      // Blue when the forecast moved since the Base Plan (FAR-77 §5.5).
                      const ch = demandChange(forecastChanges, r.itemNumber, m.iso)
                      const cell = (
                        <Box component="td" key={`${m.key}d`} data-demand-changed={ch ? 'true' : undefined}
                          sx={{ ...cellSx, ...t, ...LINE_BORDER, ...(ch ? { color: '#2563EB', fontWeight: 700 } : {}) }}>
                          {num(r[`${m.key}_fcst`])}
                        </Box>
                      )
                      return ch ? <Tooltip key={`${m.key}d`} title={ch.tooltip}>{cell}</Tooltip> : cell
                    })(),
                    // §5.3: no amber marker on this tab; the header icon flags review.
                    viewOnly ? (
                      <Box component="td" key={`${m.key}u`} sx={{ ...cellSx, ...t }}>{num(r[`${m.key}_cases`])}</Box>
                    ) : <UnitsCell key={`${m.key}u`} label={`${r.itemNumber} ${m.label}`} units={r[`${m.key}_cases`]}
                      baseUnits={baseRowsByItem?.[r.itemNumber]?.[`${m.key}_cases`]} edited={!!r[`${m.key}_cellEdited`]}
                      roundingValue={r.roundingValue} disabled={readOnly || !onEditUnits} lockedTip={readOnly ? lockMessage : ''}
                      onCommit={(v) => onEditUnits(r, m, v)} sx={{ ...cellSx, ...t }} />,
                    <Box component="td" key={`${m.key}i`} sx={{ ...cellSx, ...t, color: 'text.secondary' }}>{num(r[`${m.key}_endInv`])}</Box>,
                    // §4.3: out-of-band DOS tints the cell (red / amber) with ▼ / ▲.
                    <Box component="td" key={`${m.key}s`} data-dos-status={st}
                      sx={{ ...cellSx, ...t, ...(dosCellStyle(st) || {}), fontWeight: st === 'below' || st === 'above' ? 700 : 400 }}>
                      {st === 'below' && <Box component="span" aria-label="below Min" sx={{ mr: 0.25, fontSize: '0.6rem' }}>▼</Box>}
                      {st === 'above' && <Box component="span" aria-label="above Max" sx={{ mr: 0.25, fontSize: '0.6rem' }}>▲</Box>}
                      {dos == null ? '—' : Math.round(dos)}
                    </Box>,
                  ]
                })}
              </Box>
              )
            })}
          </tbody>
          {/* §4.6 footer totals, pinned below the rows. */}
          <Box component="tfoot" sx={{ position: 'sticky', bottom: 0, zIndex: 3 }} data-testid={`${testId}-footer`}>
            {[
              ['Total', (t) => [abbreviate(t.demand), abbreviate(t.units), abbreviate(t.inventory), t.avgDos == null ? '—' : Math.round(t.avgDos)], {}],
              ['Plan vs Demand', (t) => ['', abbreviate(t.cumPlanVsDemand), t.cumPlanVsDemandPct == null ? '—' : `${Math.round(t.cumPlanVsDemandPct)}%`, ''], { color: 'text.secondary' }],
              ['Δ vs Base Plan', (t) => ['', abbreviate(t.deltaVsBase), t.deltaVsBasePct == null ? '—' : `${Math.round(t.deltaVsBasePct)}%`, ''], { delta: true }],
            ].filter(([label]) => !(viewOnly && label === 'Δ vs Base Plan')).map(([label, fn, opt], k) => (
              <Box component="tr" key={label}>
                <Box component="td" colSpan={frozen}
                  sx={{ ...stick(0, FOOT), fontWeight: 700, fontSize: '0.8rem', textAlign: 'left', px: 2, ...(k === 0 ? { borderTop: '2px solid', borderTopColor: 'divider' } : {}) }}>
                  {label === 'Total' ? `Total · all ${rows.length} SKUs` : label === 'Plan vs Demand' ? 'Plan vs Demand · cumulative' : label}
                  {label === 'Total' && totals.excludedPallet > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
                      Cases only · {totals.excludedPallet} pallet item{totals.excludedPallet === 1 ? '' : 's'} not included
                    </Typography>
                  )}
                </Box>
                {months.map((m, mi) => {
                  const t = totals.perMonth[m.key] || {}
                  const color = opt.delta ? deltaColor(t.deltaVsBase) : (opt.color || 'inherit')
                  return fn(t).map((v, i) => (
                    <Box component="td" key={`${m.key}${i}`} data-delta={opt.delta ? String(t.deltaVsBase ?? '') : undefined}
                      sx={{
                        ...cellSx, fontWeight: 700, color, bgcolor: mi % 2 === 1 ? FOOT_TINT : FOOT,
                        ...(i === 0 ? LINE_BORDER : {}), ...(k === 0 ? { borderTop: '2px solid', borderTopColor: 'divider' } : {}),
                      }}>{v}</Box>
                  ))
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={(_e, reason) => { if (reason !== 'clickaway') setToast('') }}
        message={toast} data-testid={`${testId}-toast`} />
    </Card>
  )
}
